import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { Font } from 'types'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'

export interface Data {
  font: Font
}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

interface Props {
  workspaceId: number
}

type Variables = {
  name?: string
  source_url: string
}

export const createFont = async ({ workspaceId, ...variables }: Props & Variables) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/fonts`, {
    method: 'POST',
    body: { ...variables },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to create font', {
      metaData: {
        variables,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useCreateFont = (options?: Options) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  return useMutation<Data, unknown, Variables>(
    'createFont',
    async (variables) => {
      return createFont({ workspaceId: currentWorkspaceId, ...variables })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'fonts', 'list'])
      },
      ...options,
    },
  )
}
